import React, { useState, useEffect } from 'react';
import { ChevronDown, Heart, Book, Music, Video, ExternalLink, Menu, X } from 'lucide-react';
import BookProduct from './components/BookProduct';
import MusicProduct from './components/MusicProduct';
import VideoProduct from './components/VideoProduct';
import AfricanPattern from './components/AfricanPattern';
import siteContent from './data/siteContent';
import './styles/main.css';

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState('50'); // Default donation amount

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Extract data from siteContent
  const { config, navigation, hero, about, activities, publications, donate, footer } = siteContent;

  return (
    <div className="achh-website">
      <AfricanPattern />
      
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo">
  {config.logoUrl && (
    <img src={config.logoUrl} alt={config.siteFullName} className="logo-image" />
  )}
  <span className="logo-text">
    {config.showFullName ? config.siteFullName : config.siteName}
  </span>
</div>
          
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <ul className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            {navigation.links.map((link, index) => (
              <li key={index}>
                <a href={link.href} onClick={() => setActiveSection(link.href.substring(1))}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="title-line">{hero.title.line1}</span>
            <span className="title-line">{hero.title.line2}</span>
          </h1>
          <p className="hero-subtitle">{hero.subtitle}</p>
          <div className="hero-cta">
            <a href={hero.buttons.primary.link} className="cta-btn primary">
              {hero.buttons.primary.text}
            </a>
            <a href={hero.buttons.secondary.link} className="cta-btn secondary">
              {hero.buttons.secondary.text}
            </a>
          </div>
        </div>
        <div className="hero-decoration">
          <img src="/images/hero/decoration1.png" alt="" className="doodle-img doodle-1" />
          <img src="/images/hero/decoration2.png" alt="" className="doodle-img doodle-2" />
          <img src="/images/hero/decoration3.png" alt="" className="doodle-img doodle-3" />
          <img src="/images/hero/decoration4.png" alt="" className="doodle-img doodle-4" />
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
        <a href="#about" className="scroll-indicator">
          <ChevronDown size={32} />
        </a>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{about.title}</h2>
            <div className="title-underline"></div>
            <img src="https://cdn.doodleipsum.com/700/hand-drawn?bg=DAA520&i=mission-doodle" alt="" className="section-doodle" />
          </div>
          <div className="about-content">
            <div className="about-text">
              {about.paragraphs.map((paragraph, index) => (
                <p key={index} className={index === 0 ? "lead-text" : ""}>
                  {paragraph}
                </p>
              ))}
              <img src="https://cdn.doodleipsum.com/700/avatar-2?bg=E27D5F&i=about-doodle-inline" alt="" className="inline-doodle" />
            </div>
            <div className="about-stats">
  {about.stats.map((stat, index) => (
    <div key={index} className="stat-card">
      {/* No icon/image here */}
      <div className="stat-number">{stat.number}</div>
      <div className="stat-label">{stat.label}</div>
    </div>
  ))}
</div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section id="activities" className="activities">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{activities.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{activities.subtitle}</p>
            <img src="https://cdn.doodleipsum.com/700/abstract?bg=DAA520&i=activities-header-doodle" alt="" className="section-doodle" />
          </div>
          <div className="activities-grid">
            {activities.items.map((activity, index) => (
              <div key={index} className="activity-card" style={{ animationDelay: `${index * 0.1}s` }}>
  {activity.imageUrl ? (
    <div className="activity-image-container">
      <img src={activity.imageUrl} alt={activity.title} className="activity-image" />
    </div>
  ) : (
    <div className="activity-icon">{activity.icon}</div>
  )}
  <h3 className="activity-title">{activity.title}</h3>
  <p className="activity-description">{activity.description}</p>
  <a href="#contact" className="activity-link">
    Learn More <ExternalLink size={16} />
  </a>
</div>
            ))}
          </div>
          <img src="https://cdn.doodleipsum.com/700/avatar-5?bg=5D3A1A&i=activities-bottom-doodle" alt="" className="section-bottom-doodle" />
        </div>
      </section>




      {/* Publications Section */}
      <section id="publications" className="publications">
        <div className="container-wide">
          <div className="section-header">
            <h2 className="section-title">{publications.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{publications.subtitle}</p>
            <img src="https://cdn.doodleipsum.com/700/hand-drawn?bg=DAA520&i=publications-header-doodle" alt="" className="section-doodle" />
          </div>

          {/* DVDs */}
          <div className="publication-category">
            <div className="category-header">
              <Video size={32} />
              <h3 className="category-title">DVDs</h3>
              <img src="https://cdn.doodleipsum.com/300/abstract?bg=E27D5F&i=dvds-doodle" alt="" className="category-doodle" />
            </div>
            <div className="products-grid">
              {publications.dvds.map((dvd, index) => (
                <VideoProduct key={index} {...dvd} />
              ))}
            </div>
          </div>

          {/* Folktale CDs */}
          <div className="publication-category">
            <div className="category-header">
              <Music size={32} />
              <h3 className="category-title">Folktale CDs</h3>
              <img src="https://cdn.doodleipsum.com/300/face?bg=DAA520&i=folktales-doodle" alt="" className="category-doodle" />
            </div>
            <div className="products-grid">
              {publications.folktaleCDs.map((cd, index) => (
                <MusicProduct key={index} {...cd} />
              ))}
            </div>
          </div>

          {/* Music CDs */}
          <div className="publication-category">
            <div className="category-header">
              <Music size={32} />
              <h3 className="category-title">Music CDs</h3>
              <img src="https://cdn.doodleipsum.com/300/hand-drawn?bg=5D3A1A&i=music-doodle" alt="" className="category-doodle" />
            </div>
            <div className="products-grid">
              {publications.musicCDs.map((cd, index) => (
                <MusicProduct key={index} {...cd} />
              ))}
            </div>
          </div>

          {/* Books */}
          <div className="publication-category">
            <div className="category-header">
              <Book size={32} />
              <h3 className="category-title">Books</h3>
              <img src="https://cdn.doodleipsum.com/300/avatar-3?bg=E27D5F&i=books-doodle" alt="" className="category-doodle" />
            </div>
            <div className="products-grid">
              {publications.books.map((book, index) => (
                <BookProduct key={index} {...book} />
              ))}
            </div>
          </div>
        </div>
      </section>
{/* Contact Section */}
<section id="contact" className="contact">
  <div className="container">
    <div className="section-header">
      <h2 className="section-title">Get in Touch</h2>
      <div className="title-underline"></div>
      <p className="section-subtitle">Have questions? Want to get involved? We'd love to hear from you!</p>
    </div>
    
    <div className="contact-content">
      <div className="contact-info">
        <div className="contact-item">
          <h3>📧 Email</h3>
          <p><a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a></p>
        </div>
        <div className="contact-item">
          <h3>📞 Phone</h3>
          <p><a href={`tel:${config.contactPhone}`}>{config.contactPhone}</a></p>
        </div>
              </div>
      
      <div className="contact-form">
        <form>
          <input type="text" placeholder="Your Name" required />
          <input type="email" placeholder="Your Email" required />
          <textarea placeholder="Your Message" rows="5" required></textarea>
          <button type="submit" className="cta-btn primary">Send Message</button>
        </form>
      </div>
    </div>
  </div>
</section>
      {/* Donate Section */}
      <section id="donate" className="donate">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{donate.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{donate.subtitle}</p>
            <img src="https://cdn.doodleipsum.com/700/avatar?bg=DAA520&i=donate-header-doodle" alt="" className="section-doodle section-doodle-light" />
          </div>
          
          <div className="donate-content">
            <div className="donate-info">
              <img src="https://cdn.doodleipsum.com/400/face?bg=E27D5F&i=donate-side-doodle" alt="" className="donate-side-doodle" />
              <h3>{donate.infoTitle}</h3>
              <ul className="donate-benefits">
                {donate.benefits.map((benefit, index) => (
                  <li key={index}>
                    <Heart size={20} className="benefit-icon" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="donate-form-container">
              <img src="https://cdn.doodleipsum.com/300/hand-drawn?bg=DAA520&i=donate-form-doodle" alt="" className="form-doodle" />
              <div className="donation-amounts">
                <h4>{donate.amountTitle}</h4>
                <div className="amount-grid">
                  {donate.amounts.map((amount, index) => {
                    const amountValue = amount.replace('$', '').replace('Custom', '');
                    return (
                      <button 
                        key={index} 
                        className={`amount-btn ${selectedAmount === amountValue ? 'active' : ''}`}
                        onClick={() => {
                          if (amount !== 'Custom') {
                            setSelectedAmount(amountValue);
                          }
                        }}
                        type="button"
                      >
                        {amount}
                      </button>
                    );
                  })}
                </div>
              </div>

              <form action="https://www.paypal.com/donate" method="post" target="_blank" className="donate-form">
                <input type="hidden" name="business" value={config.paypalEmail} />
                <input type="hidden" name="no_recurring" value="0" />
                <input type="hidden" name="item_name" value="Support The African Cultural Heritage Hub Mission" />
                <input type="hidden" name="amount" value={selectedAmount} />
                <input type="hidden" name="currency_code" value="USD" />
                
                <button type="submit" className="donate-btn">
                  <Heart size={20} />
                  {donate.buttonText}
                </button>
              </form>

              <p className="donate-note">{donate.taxNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            {footer.sections.map((section, index) => (
              <div key={index} className="footer-section">
                <h4>{section.title}</h4>
                {section.content && <p>{section.content}</p>}
                {section.links && (
                  <ul>
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <a href={link.href}>{link.text}</a>
                      </li>
                    ))}
                  </ul>
                )}
                {section.lines && section.lines.map((line, lineIndex) => (
                  <p key={lineIndex}>{line}</p>
                ))}
              </div>
            ))}
          </div>
          <div className="footer-bottom">
            <p>{footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
